import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const DOC_TYPES = [
  { id: "verifier_report", label: "Verifier report" },
  { id: "pdd", label: "Project Design Doc (PDD)" },
  { id: "audit", label: "Independent audit" },
  { id: "map", label: "Property map" },
  { id: "satellite", label: "Satellite imagery" },
  { id: "other", label: "Other" },
];

const CURRENT_YEAR = new Date().getFullYear();

export default function ProducerListingModal({ listing, onClose, onSaved }) {
  const isNew = !listing || !listing.id;
  const readOnly = listing && ["pending_review", "approved", "rejected"].includes(listing.status);
  const [form, setForm] = useState({
    name: listing?.name || "",
    region: listing?.region || "",
    methodology: listing?.methodology || "REDD+",
    tons_available: listing?.tons_available || 1000,
    vintage: listing?.vintage || CURRENT_YEAR,
    ask_price_usd_per_ton: listing?.ask_price_usd_per_ton || 35.80,
    description: listing?.description || "",
    latitude: listing?.latitude ?? "",
    longitude: listing?.longitude ?? "",
    hectares: listing?.hectares ?? "",
    bmv_isin_pre: listing?.bmv_isin_pre || "",
    property_name: listing?.property_name || "",
  });
  const [current, setCurrent] = useState(listing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitNote, setSubmitNote] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => ({
    ...form,
    tons_available: Number(form.tons_available),
    vintage: Number(form.vintage),
    ask_price_usd_per_ton: Number(form.ask_price_usd_per_ton),
    latitude: form.latitude === "" ? null : Number(form.latitude),
    longitude: form.longitude === "" ? null : Number(form.longitude),
    hectares: form.hectares === "" ? null : Number(form.hectares),
  });

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/producer/listings", buildPayload());
        setCurrent(data);
        toast.success("Draft saved");
      } else {
        const { data } = await api.patch(`/producer/listings/${current.id}`, buildPayload());
        setCurrent(data);
        toast.success("Draft updated");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (e, docType) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !current?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/producer/listings/${current.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Refresh listing
      const { data } = await api.get(`/producer/listings/${current.id}`);
      setCurrent(data);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/producer/listings/${current.id}/documents/${docId}`);
      const { data } = await api.get(`/producer/listings/${current.id}`);
      setCurrent(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const submitForReview = async () => {
    if (!current?.id) {
      toast.error("Save the draft first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/producer/listings/${current.id}/submit`, { note: submitNote });
      toast.success("Submitted for compliance review");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const activeDocs = useMemo(
    () => (current?.documents || []).filter((d) => !d.is_deleted),
    [current]
  );
  const canSubmit = current?.id && activeDocs.length > 0 && !readOnly &&
    ["draft", "needs_info"].includes(current.status);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-ink/60" onClick={onClose} data-testid="listing-modal">
      <div
        className="w-full max-w-3xl bg-bg overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-bg border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag">{isNew ? "New listing" : `Listing · ${current?.status?.replace("_", " ")}`}</div>
            <div className="font-serif text-xl">{form.name || "Untitled UCS listing"}</div>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider border border-line hover:border-ink"
            data-testid="listing-modal-close"
          >
            ✕ Close
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {current?.status === "needs_info" && current?.review_notes && (
            <div className="border border-danger/40 bg-danger/5 text-danger p-4 text-sm">
              <div className="label-tag text-danger mb-1">Reviewer note</div>
              {current.review_notes}
            </div>
          )}
          {current?.status === "rejected" && (
            <div className="border border-danger/40 bg-danger/5 text-danger p-4 text-sm">
              <div className="label-tag text-danger mb-1">Rejected</div>
              {current.review_notes || "Contact compliance for details."}
            </div>
          )}
          {current?.status === "approved" && (
            <div className="border border-leaf/40 bg-leaf/5 text-leaf p-4 text-sm">
              <div className="label-tag text-leaf mb-1">Approved</div>
              Your listing is now live on the marketplace.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Project name" testid="lst-name">
              <input className="input-field" value={form.name} disabled={readOnly}
                     onChange={(e) => set("name", e.target.value)} data-testid="lst-name" />
            </Field>
            <Field label="Property name" testid="lst-prop">
              <input className="input-field" value={form.property_name} disabled={readOnly}
                     onChange={(e) => set("property_name", e.target.value)} data-testid="lst-property" />
            </Field>
            <Field label="Region">
              <input className="input-field" value={form.region} disabled={readOnly}
                     onChange={(e) => set("region", e.target.value)} data-testid="lst-region" />
            </Field>
            <Field label="Methodology">
              <select className="input-field" value={form.methodology} disabled={readOnly}
                      onChange={(e) => set("methodology", e.target.value)} data-testid="lst-methodology">
                {["REDD+", "IFM", "ARR", "ALM", "Blue Carbon", "Engineered Removal", "Other"].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>
            <Field label="Volume (tCO₂)">
              <input type="number" className="input-field" value={form.tons_available} disabled={readOnly}
                     onChange={(e) => set("tons_available", e.target.value)} data-testid="lst-tons" />
            </Field>
            <Field label="Vintage year">
              <input type="number" className="input-field" value={form.vintage} disabled={readOnly}
                     onChange={(e) => set("vintage", e.target.value)} data-testid="lst-vintage" />
            </Field>
            <Field label="Ask price (USD/ton)">
              <input type="number" step="0.01" className="input-field" value={form.ask_price_usd_per_ton} disabled={readOnly}
                     onChange={(e) => set("ask_price_usd_per_ton", e.target.value)} data-testid="lst-price" />
            </Field>
            <Field label="Hectares">
              <input type="number" step="0.01" className="input-field" value={form.hectares} disabled={readOnly}
                     onChange={(e) => set("hectares", e.target.value)} data-testid="lst-hectares" />
            </Field>
            <Field label="Latitude">
              <input type="number" step="0.0001" className="input-field" value={form.latitude} disabled={readOnly}
                     onChange={(e) => set("latitude", e.target.value)} data-testid="lst-lat" />
            </Field>
            <Field label="Longitude">
              <input type="number" step="0.0001" className="input-field" value={form.longitude} disabled={readOnly}
                     onChange={(e) => set("longitude", e.target.value)} data-testid="lst-lng" />
            </Field>
            <Field label="BMV ISIN (pre-registration, optional)">
              <input className="input-field" value={form.bmv_isin_pre} disabled={readOnly}
                     onChange={(e) => set("bmv_isin_pre", e.target.value)} data-testid="lst-isin" />
            </Field>
          </div>
          <Field label="Description">
            <textarea className="input-field min-h-[100px]" value={form.description} disabled={readOnly}
                      onChange={(e) => set("description", e.target.value)} data-testid="lst-desc" />
          </Field>

          {!readOnly && (
            <div className="flex justify-end">
              <button
                onClick={save}
                disabled={saving || readOnly}
                data-testid="lst-save"
                className="px-5 py-3 border border-ink text-[11px] font-mono uppercase tracking-wider hover:bg-ink hover:text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : isNew ? "Save draft →" : "Update draft →"}
              </button>
            </div>
          )}

          {/* Docs section — only when saved */}
          {current?.id && (
            <div className="border-t border-line pt-6">
              <div className="label-tag mb-3">Documentation ({activeDocs.length})</div>
              <div className="space-y-2 mb-4">
                {activeDocs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border border-line bg-surface p-3 text-sm">
                    <div className="min-w-0">
                      <div className="truncate">{d.original_filename}</div>
                      <div className="text-[11px] font-mono text-ink2 mt-1">
                        {d.doc_type.replace("_", " ")} · {(d.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0 ml-3">
                      {!readOnly && (
                        <button
                          onClick={() => removeDoc(d.id)}
                          className="text-[10px] font-mono uppercase text-danger hover:underline"
                          data-testid={`doc-remove-${d.id}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {activeDocs.length === 0 && (
                  <div className="text-ink2 text-sm italic">No documents attached yet.</div>
                )}
              </div>

              {!readOnly && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {DOC_TYPES.map((dt) => (
                    <label
                      key={dt.id}
                      className="border border-dashed border-line hover:border-ink cursor-pointer p-3 text-center text-[11px] font-mono uppercase tracking-wider"
                      data-testid={`upload-${dt.id}`}
                    >
                      + {dt.label}
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.gif,.svg"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => uploadDoc(e, dt.id)}
                      />
                    </label>
                  ))}
                </div>
              )}
              {uploading && <div className="mt-3 text-[11px] font-mono text-ink2">Uploading…</div>}
            </div>
          )}

          {/* Submit for review */}
          {canSubmit && (
            <div className="border-t border-line pt-6 space-y-3">
              <div className="label-tag">Submit for compliance review</div>
              <textarea
                className="input-field min-h-[70px]"
                placeholder="Optional note to reviewers…"
                value={submitNote}
                onChange={(e) => setSubmitNote(e.target.value)}
                data-testid="lst-submit-note"
              />
              <div className="flex justify-end">
                <button
                  onClick={submitForReview}
                  disabled={submitting}
                  data-testid="lst-submit-review"
                  className="px-6 py-3 bg-ink text-white text-[11px] font-mono uppercase tracking-wider hover:bg-black disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Submit for review →"}
                </button>
              </div>
            </div>
          )}

          {/* Status history */}
          {(current?.status_history || []).length > 0 && (
            <div className="border-t border-line pt-6">
              <div className="label-tag mb-3">Status history</div>
              <div className="space-y-2 text-[12px] font-mono">
                {[...current.status_history].reverse().map((h) => (
                  <div key={`${h.status}-${h.changed_at}`} className="flex justify-between gap-3 border-l-2 border-line pl-3">
                    <div>
                      <div className="text-ink uppercase">{h.status.replace("_", " ")}</div>
                      {h.note && <div className="text-ink2">{h.note}</div>}
                    </div>
                    <div className="text-ink2 text-right">
                      <div>{new Date(h.changed_at).toLocaleString()}</div>
                      <div className="text-[10px]">{h.changed_by_email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-tag block mb-1">{label}</span>
      {children}
    </label>
  );
}
